import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { RecommendationNode } from './useRecommendations';

export interface AlgorithmFactors {
  learningStyleMatch: number;    // 0-1 score
  difficultyProgression: number; // 0-1 score  
  interestAlignment: number;     // 0-1 score
  timeOptimization: number;      // 0-1 score
  prerequisiteReadiness: number; // 0-1 score
  peerSimilarity: number;        // 0-1 score
}

export interface PersonalizedMetadata {
  estimatedEngagement: number;
  difficultyMatch: string;
  learningOutcomes: string[];
  studyTimeEstimate: number;
  optimalStudyTime: string;
}

export interface AdvancedRecommendation extends RecommendationNode {
  algorithmFactors: AlgorithmFactors;
  confidenceScore: number;
  recommendationReason: string;
  personalizedMetadata: PersonalizedMetadata;
  contextualTags: string[];
  priorityScore: number;
  freshness: 'new' | 'updated' | 'trending' | 'stable';
}

export interface RecommendationContext {
  timeAvailable?: number;
  currentDiscipline?: string;
  learningGoal?: 'exploration' | 'mastery' | 'review' | 'skill_building';
  sessionType?: 'quick' | 'deep' | 'mixed';
  limit?: number;
}

export interface UserLearningProfile {
  learningStyle: string;
  preferredDifficulty: number;
  studyTimePreference: number;
  strongSubjects: string[];
  weakSubjects: string[];
  averageSessionLength: number;
  completionRate: number;
  retentionRate: number;
  learningVelocity: number;
  peakPerformanceTime: string;
  recentTopics: string[];
}

export const useAdvancedRecommendations = (context?: RecommendationContext) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['advanced-recommendations', user?.id, context],
    queryFn: async (): Promise<AdvancedRecommendation[]> => {
      if (!user?.id) return [];

      console.log('🚀 Generating advanced recommendations with context:', context);

      // Step 1: Get comprehensive user learning profile
      const learningProfile = await getUserLearningProfile(user.id);

      // Step 2: Get candidate content pool based on context
      const candidates = await getCandidateContent(user.id, context);

      if (!candidates || candidates.length === 0) {
        return [];
      }

      // Step 3: Apply multi-factor scoring to each candidate
      const scoredRecommendations = await Promise.all(
        candidates.map(async (candidate) => {
          const scores = await calculateAdvancedRecommendationScores(
            candidate, 
            learningProfile, 
            context
          );
          return await enhanceRecommendationWithAdvancedMetadata(candidate, scores, learningProfile, context);
        })
      );

      // Step 4: Rank and filter based on priority score
      const rankedRecommendations = scoredRecommendations
        .sort((a, b) => b.priorityScore - a.priorityScore)
        .slice(0, context?.limit || 10);

      return rankedRecommendations;
    },
    enabled: !!user?.id,
    staleTime: 3 * 60 * 1000, // 3 minutes for fresh recommendations
  });
};

// Get comprehensive user learning profile
async function getUserLearningProfile(userId: string): Promise<UserLearningProfile> {
  // Get user preferences
  const { data: prefs } = await supabase
    .from('user_preferences')
    .select('*')
    .eq('user_id', userId)
    .single();

  // Get user progress history for analytics
  const { data: progressHistory } = await supabase
    .from('user_progress')
    .select(`
      *,
      nodes!inner(
        difficulty_level,
        estimated_study_time,
        disciplines!inner(name)
      )
    `)
    .eq('user_id', userId)
    .order('completed_at', { ascending: false })
    .limit(50);

  // Analyze progress patterns
  const sessions = progressHistory || [];
  const completedSessions = sessions.filter(s => s.status === 'completed');
  const averageTime = sessions.reduce((acc, s) => acc + (s.time_spent_seconds || 0), 0) / (sessions.length || 1) / 60;

  // Calculate subject performance
  const subjectPerformance = sessions.reduce((acc, session) => {
    const subject = session.nodes?.disciplines?.name;
    if (subject) {
      if (!acc[subject]) acc[subject] = { total: 0, completed: 0 };
      acc[subject].total++;
      if (session.status === 'completed') acc[subject].completed++;
    }
    return acc;
  }, {} as Record<string, { total: number; completed: number }>);

  const strongSubjects = Object.entries(subjectPerformance)
    .filter(([_, perf]) => perf.completed / perf.total > 0.75)
    .map(([subject]) => subject);

  const weakSubjects = Object.entries(subjectPerformance)
    .filter(([_, perf]) => perf.completed / perf.total < 0.5)
    .map(([subject]) => subject);

  return {
    learningStyle: prefs?.learning_style || 'mixed',
    preferredDifficulty: prefs?.preferred_difficulty || 2,
    studyTimePreference: prefs?.study_time_availability || 30,
    strongSubjects,
    weakSubjects,
    averageSessionLength: Math.max(15, Math.min(90, averageTime)),
    completionRate: sessions.length > 0 ? completedSessions.length / sessions.length : 0.7,
    retentionRate: 0.7, // Simplified
    learningVelocity: sessions.length > 0 ? sessions.length / 7 : 1,
    peakPerformanceTime: 'afternoon', // Default, could be derived from historical data
    recentTopics: sessions.slice(0, 5).map(s => s.nodes?.disciplines?.name).filter(Boolean) as string[]
  };
}

// Get candidate content based on context
async function getCandidateContent(userId: string, context?: RecommendationContext): Promise<any[]> {
  let query = supabase
    .from('nodes')
    .select(`
      id,
      title,
      node_type,
      difficulty_level,
      estimated_study_time,
      content_type,
      description,
      disciplines!inner(name),
      created_at,
      updated_at
    `)
    .eq('status', 'published');

  // Apply context filters
  if (context?.timeAvailable) {
    query = query.lte('estimated_study_time', context.timeAvailable);
  }

  if (context?.currentDiscipline) {
    query = query.eq('disciplines.name', context.currentDiscipline);
  }

  query = query.limit(30);

  const { data: nodes, error } = await query;
  if (error) throw error;

  return nodes || [];
}

// Calculate advanced recommendation scores
async function calculateAdvancedRecommendationScores(
  content: any,
  userProfile: UserLearningProfile,
  context?: RecommendationContext
): Promise<AlgorithmFactors> {
  
  const learningStyleMatch = calculateLearningStyleMatch(content, userProfile);
  const difficultyProgression = calculateDifficultyProgression(content, userProfile);
  const interestAlignment = calculateInterestAlignment(content, userProfile);
  const timeOptimization = calculateTimeOptimization(content, userProfile, context);
  const prerequisiteReadiness = 0.8; // Simplified
  const peerSimilarity = 0.7; // Simplified

  return {
    learningStyleMatch,
    difficultyProgression,
    interestAlignment,
    timeOptimization,
    prerequisiteReadiness,
    peerSimilarity
  };
}

// Learning style matching algorithm
function calculateLearningStyleMatch(content: any, profile: UserLearningProfile): number {
  const styleMatches = {
    visual: { diagram: 0.9, video: 0.8, article: 0.6 },
    auditory: { podcast: 0.9, lecture: 0.8, article: 0.5 },
    kinesthetic: { simulation: 0.9, exercise: 0.85, article: 0.4 },
    reading: { article: 0.9, book: 0.95, video: 0.5 },
    mixed: { article: 0.7, video: 0.8, exercise: 0.75 }
  };

  const contentType = content.content_type || 'article';
  return styleMatches[profile.learningStyle]?.[contentType] || 0.7;
}

// Difficulty progression calculation
function calculateDifficultyProgression(content: any, profile: UserLearningProfile): number {
  const contentDifficulty = content.difficulty_level;
  const userPreferred = profile.preferredDifficulty;
  
  let targetDifficulty = userPreferred;
  if (profile.completionRate > 0.8) {
    targetDifficulty = Math.min(5, userPreferred + 0.5);
  } else if (profile.completionRate < 0.6) {
    targetDifficulty = Math.max(1, userPreferred - 0.5);
  }

  const distance = Math.abs(contentDifficulty - targetDifficulty);
  return Math.max(0, 1 - (distance / 3));
}

// Interest alignment calculation
function calculateInterestAlignment(content: any, profile: UserLearningProfile): number {
  const discipline = content.disciplines?.name;
  
  if (profile.strongSubjects.includes(discipline)) return 0.9;
  if (profile.recentTopics.includes(discipline)) return 0.7;
  if (profile.weakSubjects.includes(discipline)) return 0.4;
  
  return 0.6;
}

// Time optimization calculation
function calculateTimeOptimization(
  content: any, 
  profile: UserLearningProfile, 
  context?: RecommendationContext
): number {
  const contentTime = content.estimated_study_time;
  const availableTime = context?.timeAvailable || profile.studyTimePreference;

  if (contentTime <= availableTime && contentTime >= availableTime * 0.7) return 1.0;
  if (contentTime > availableTime * 1.5) return 0.3;
  if (contentTime < availableTime * 0.3) return 0.5;
  
  return 0.6;
}

// Enhance recommendation with advanced metadata
async function enhanceRecommendationWithAdvancedMetadata(
  content: any,
  factors: AlgorithmFactors,
  profile: UserLearningProfile,
  context?: RecommendationContext
): Promise<AdvancedRecommendation> {
  
  const confidenceScore = calculateOverallConfidenceScore(factors);
  const recommendationReason = generateRecommendationReason(factors, content, profile);
  const personalizedMetadata = calculatePersonalizedMetadata(content, factors, profile);
  const contextualTags = generateContextualTags(factors, content, context);
  const priorityScore = calculatePriorityScore(factors, content, context);
  const freshness = determineFreshness(content);

  return {
    node_id: content.id,
    title: content.title,
    difficulty_level: content.difficulty_level,
    estimated_study_time: content.estimated_study_time,
    prerequisite_status: {
      all_completed: factors.prerequisiteReadiness > 0.8,
      completion_rate: factors.prerequisiteReadiness,
      has_prerequisites: factors.prerequisiteReadiness < 1.0,
      missing_prerequisites: [],
      completed_prerequisites: []
    },
    recommendation_score: confidenceScore.toString(),
    content_type: content.content_type,
    node_type: content.node_type,
    discipline_name: content.disciplines?.name,
    algorithmFactors: factors,
    confidenceScore,
    recommendationReason,
    personalizedMetadata,
    contextualTags,
    priorityScore,
    freshness
  };
}

function calculateOverallConfidenceScore(factors: AlgorithmFactors): number {
  const weights = {
    learningStyleMatch: 0.2,
    difficultyProgression: 0.25,
    interestAlignment: 0.2,
    timeOptimization: 0.15,
    prerequisiteReadiness: 0.15,
    peerSimilarity: 0.05
  };

  const weightedScore = Object.entries(factors).reduce((acc, [key, value]) => {
    return acc + (value * weights[key as keyof typeof weights]);
  }, 0);

  return Math.round(weightedScore * 100);
}

function generateRecommendationReason(
  factors: AlgorithmFactors, 
  content: any, 
  profile: UserLearningProfile
): string {
  const reasons = [];

  if (factors.learningStyleMatch > 0.8) reasons.push(`perfect for ${profile.learningStyle} learners`);
  if (factors.difficultyProgression > 0.8) reasons.push('ideal difficulty progression');
  if (factors.interestAlignment > 0.8) reasons.push('matches your interests');
  if (factors.timeOptimization > 0.8) reasons.push('fits your available time');

  if (reasons.length === 0) return 'recommended based on your learning profile';
  return `Recommended because it's ${reasons.slice(0, 2).join(' and ')}`;
}

function calculatePersonalizedMetadata(
  content: any, 
  factors: AlgorithmFactors, 
  profile: UserLearningProfile
): PersonalizedMetadata {
  const estimatedEngagement = (factors.learningStyleMatch + factors.interestAlignment) / 2;
  const difficultyMatch = factors.difficultyProgression > 0.8 ? 'Perfect match' :
                         factors.difficultyProgression > 0.6 ? 'Good match' : 'May be challenging';
  const learningOutcomes = ['Expand your knowledge'];
  const studyTimeEstimate = Math.round(content.estimated_study_time * (2 - factors.difficultyProgression));
  const optimalStudyTime = profile.peakPerformanceTime;

  return {
    estimatedEngagement,
    difficultyMatch,
    learningOutcomes,
    studyTimeEstimate,
    optimalStudyTime
  };
}

function generateContextualTags(
  factors: AlgorithmFactors, 
  content: any, 
  context?: RecommendationContext
): string[] {
  const tags = [];

  if (factors.learningStyleMatch > 0.8) tags.push('Style Match');
  if (factors.difficultyProgression > 0.8) tags.push('Perfect Level');
  if (factors.timeOptimization > 0.8) tags.push('Time Optimized');
  if (factors.prerequisiteReadiness > 0.9) tags.push('Ready to Learn');
  if (factors.interestAlignment > 0.8) tags.push('High Interest');

  return tags;
}

function calculatePriorityScore(
  factors: AlgorithmFactors, 
  content: any, 
  context?: RecommendationContext
): number {
  let score = 0;

  score += factors.difficultyProgression * 30;
  score += factors.learningStyleMatch * 25;
  score += factors.interestAlignment * 20;
  score += factors.prerequisiteReadiness * 15;
  score += factors.timeOptimization * 10;

  return Math.round(score);
}

function determineFreshness(content: any): 'new' | 'updated' | 'trending' | 'stable' {
  const now = new Date();
  const created = new Date(content.created_at);
  const daysSinceCreated = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);

  if (daysSinceCreated <= 7) return 'new';
  if (daysSinceCreated <= 30) return 'trending';
  return 'stable';
} 